import { FetchHttpClient } from "@effect/platform";
import type { TeamNotification as TeamNotificationType } from "@redotech/effect-api-shared/models/team/notification";
import { Effect, Fiber, Schedule, Stream } from "effect";
import { useEffect, useRef, useState } from "react";
import { createNotificationStream } from "./notification-stream-client";

// More aggressive early retries upfront for better UX on brief connection hiccups
const fibonacciReconnectSchedule = Schedule.fibonacci("1 second").pipe(
  Schedule.compose(Schedule.recurs(10)), // 1, 1, 2,... 55, 88 seconds
);

export interface StreamConnectionOptions {
  url: string;
  token: string | undefined;
  onNotification: (notification: TeamNotificationType) => void;
  onConnected?: () => void;
}

export interface StreamConnectionResult {
  isConnected: boolean;
}

/**
 * Hook that manages a streaming connection with auto-reconnect.
 *
 * Uses Effect streams to:
 * - Establish an HTTP streaming connection
 * - Parse and decode SSE events into typed TeamNotifications
 * - Auto-reconnect with Fibonacci backoff on errors
 */
export function useStreamConnection({
  url,
  token,
  onNotification,
  onConnected,
}: StreamConnectionOptions): StreamConnectionResult {
  const [isConnected, setIsConnected] = useState(false);

  // Fiber handle for the running stream - allows interruption on cleanup
  const fiberRef = useRef<Fiber.RuntimeFiber<void, unknown> | null>(null);

  // Store callbacks in refs to avoid effect dependencies
  const onNotificationRef = useRef(onNotification);
  const onConnectedRef = useRef(onConnected);
  onNotificationRef.current = onNotification;
  onConnectedRef.current = onConnected;

  useEffect(() => {
    if (!token) {
      return;
    }

    const notificationStreamEffect = createNotificationStream(url, token).pipe(
      Stream.tap((notification) =>
        Effect.sync(() => {
          if (notification._tag === "connected") {
            setIsConnected(true);
            onConnectedRef.current?.();
          } else if (notification._tag !== "heartbeat") {
            onNotificationRef.current(notification);
          }
        }),
      ),
      Stream.tapError(() => Effect.sync(() => setIsConnected(false))),
      Stream.retry(fibonacciReconnectSchedule),
      Stream.runDrain,
      Effect.provide(FetchHttpClient.layer),
    );

    fiberRef.current = Effect.runFork(notificationStreamEffect);

    return () => {
      if (fiberRef.current) {
        Effect.runFork(Fiber.interrupt(fiberRef.current));
      }
    };
  }, [url, token]);

  return { isConnected };
}
